import React from "react";
import { Navigate } from "react-router-dom";
import { useAuthStore } from "../stores/auth.store";
import { useAuthPermissions } from "../hooks/useAuthPermissions";

interface AuthRouteProps {
  children: React.ReactNode;
  /** Full permission string (when not org-scoped). */
  requiredPermission?: string;
  /** Build permission from the active organization id. */
  orgScopedPermission?: (orgId: string) => string;
  legacyRoles?: readonly string[];
}

/**
 * Authenticated route with optional permission gate (dual-mode legacy fallback).
 */
const AuthRoute: React.FC<AuthRouteProps> = ({
  children,
  requiredPermission,
  orgScopedPermission,
  legacyRoles,
}) => {
  const { isAuthenticated } = useAuthStore();
  const { can, orgId, isPlatformAdmin } = useAuthPermissions();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const resolvedPermission =
    orgScopedPermission && orgId
      ? orgScopedPermission(orgId)
      : requiredPermission;

  if (
    resolvedPermission &&
    !isPlatformAdmin &&
    !can(resolvedPermission, legacyRoles)
  ) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

export default AuthRoute;
